// offersSlice.tsx
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@store/store';
import { Coupon } from '@eshop/types/dashboard/coupon.types.ts';
import { CouponsApi } from '@eshop/services/coupons.api.ts';
import { OffersApi } from '@eshop/services/offers.api.ts';
import { Offer, PromotionCreatePayload } from '@eshop/types/offers.types.ts';

// ------------------------------
// Helpers
// ------------------------------
const parseApiError = (err: unknown): string => {
  // axios: err.response?.data?.detail (string | [{loc,msg,type}...])
  const anyErr = err as any;  // eslint-disable-line @typescript-eslint/no-explicit-any
  const detail = anyErr?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg as string;
  return anyErr?.message || 'Unexpected error';
};

// Config tipada por-thunk (evita dependencias de una AsyncThunkConfig global)
type ThunkCfg = { state: RootState; rejectValue: string };

// ------------------------------
// State
// ------------------------------

interface OffersState {
  coupons: Coupon[];
  offers: Offer[];
  loading: boolean;
  error: string | null;
}

const initialState: OffersState = {
  coupons: [],
  offers: [],
  loading: false,
  error: null,
};

// ------------------------------
// Thunks - Coupons
// ------------------------------
export const fetchCoupons = createAsyncThunk<Coupon[], void, ThunkCfg>(
  'offers/fetchCoupons',
  async (_, { rejectWithValue }) => {
    try {
      if (!CouponsApi.list) return [];
      return await CouponsApi.list();
    } catch (err) {
      return rejectWithValue(parseApiError(err));
    }
  },
);

export const createCoupon = createAsyncThunk<Coupon, Partial<Coupon>, ThunkCfg>(
  'offers/createCoupon',
  async (payload, { rejectWithValue }) => {
    try {
      return await CouponsApi.create(payload);
    } catch (err) {
      return rejectWithValue(parseApiError(err));
    }
  },
);

export const updateCoupon = createAsyncThunk<Coupon, { id: number; data: Partial<Coupon> }, ThunkCfg>(
  'offers/updateCoupon',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      if (!CouponsApi.update) throw new Error('CouponsApi.update is not implemented');
      return await CouponsApi.update(id, data);
    } catch (err) {
      return rejectWithValue(parseApiError(err));
    }
  },
);

// ------------------------------
// Thunks - Offers (alineado a backend)
// ------------------------------
export const fetchOffers = createAsyncThunk<Offer[], void, ThunkCfg>(
  'offers/fetchOffers',
  async (_, { rejectWithValue }) => {
    try {
      return await OffersApi.list();
    } catch (err) {
      return rejectWithValue(parseApiError(err));
    }
  },
);

export const createOffer = createAsyncThunk<Offer, PromotionCreatePayload, ThunkCfg>(
  'offers/createOffer',
  async (payload, { rejectWithValue }) => {
    try {
      return await OffersApi.create(payload);
    } catch (err) {
      return rejectWithValue(parseApiError(err));
    }
  },
);

export const updateOffer = createAsyncThunk<
  Offer,
  { id: number; data: PromotionCreatePayload },
  ThunkCfg
>(
  'offers/updateOffer',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return await OffersApi.update(id, data);
    } catch (err) {
      return rejectWithValue(parseApiError(err));
    }
  },
);

export const updateOfferStatus = createAsyncThunk<Offer, { id: number; is_active: boolean }, ThunkCfg>(
  'offers/updateOfferStatus',
  async ({ id, is_active }, { rejectWithValue }) => {
    try {
      return await OffersApi.updateStatus(id, is_active);
    } catch (err) {
      return rejectWithValue(parseApiError(err));
    }
  },
);

export const removeOffer = createAsyncThunk<number, number, ThunkCfg>(
  'offers/removeOffer',
  async (id, { rejectWithValue }) => {
    try {
      await OffersApi.remove(id);
      return id;
    } catch (err) {
      return rejectWithValue(parseApiError(err));
    }
  },
);

// ------------------------------
// Slice
// ------------------------------
const offersSlice = createSlice({
  name: 'offers',
  initialState,
  reducers: {
    // Actualiza el status localmente; el backend usa "active"
    setOfferStatus: (state, action: PayloadAction<{ id: number; is_active: boolean }>) => {
      state.offers = state.offers.map((o) =>
        o.id === action.payload.id ? { ...o, active: action.payload.is_active } : o,
      );
    },
  },
  extraReducers: (builder) => {
    builder
      // Coupons
      .addCase(fetchCoupons.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCoupons.fulfilled, (state, action: PayloadAction<Coupon[]>) => {
        state.loading = false;
        state.coupons = action.payload;
      })
      .addCase(fetchCoupons.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? action.error?.message ?? 'Failed to fetch coupons';
      })
      .addCase(createCoupon.fulfilled, (state, action: PayloadAction<Coupon>) => {
        state.coupons.push(action.payload);
      })
      .addCase(createCoupon.rejected, (state, action) => {
        state.error = (action.payload as string) ?? action.error?.message ?? 'Failed to create coupon';
      })
      .addCase(updateCoupon.fulfilled, (state, action: PayloadAction<Coupon>) => {
        state.coupons = state.coupons.map((c) => (c.id === action.payload.id ? action.payload : c));
      })
      .addCase(updateCoupon.rejected, (state, action) => {
        state.error = (action.payload as string) ?? action.error?.message ?? 'Failed to update coupon';
      })

      // Offers
      .addCase(fetchOffers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOffers.fulfilled, (state, action: PayloadAction<Offer[]>) => {
        state.loading = false;
        state.offers = action.payload;
      })
      .addCase(fetchOffers.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? action.error?.message ?? 'Failed to fetch offers';
      })

      .addCase(createOffer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOffer.fulfilled, (state, action: PayloadAction<Offer>) => {
        state.loading = false;
        state.offers.push(action.payload);
      })
      .addCase(createOffer.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? action.error?.message ?? 'Failed to create offer';
      })

      .addCase(updateOffer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOffer.fulfilled, (state, action: PayloadAction<Offer>) => {
        state.loading = false;
        state.offers = state.offers.map((o) =>
          o.id === action.payload.id ? action.payload : o,
        );
      })
      .addCase(updateOffer.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? action.error?.message ?? 'Failed to update offer';
      })

      .addCase(updateOfferStatus.fulfilled, (state, action: PayloadAction<Offer>) => {
        state.offers = state.offers.map((o) => (o.id === action.payload.id ? action.payload : o));
      })
      .addCase(updateOfferStatus.rejected, (state, action) => {
        state.error = (action.payload as string) ?? action.error?.message ?? 'Failed to update offer status';
      })

      .addCase(removeOffer.fulfilled, (state, action: PayloadAction<number>) => {
        state.offers = state.offers.filter((o) => o.id !== action.payload);
      })
      .addCase(removeOffer.rejected, (state, action) => {
        state.error = (action.payload as string) ?? action.error?.message ?? 'Failed to remove offer';
      });
  },
});

export const { setOfferStatus } = offersSlice.actions;

export const selectCoupons = (state: RootState) => state?.offers?.coupons ?? [];
export const selectOffers  = (state: RootState) => state?.offers?.offers ?? [];
export const selectOffersLoading = (state: RootState) => state?.offers?.loading ?? false;
export const selectOffersError   = (state: RootState) => state?.offers?.error;

export default offersSlice.reducer;
